import _ from 'lodash'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import DEFAULTS from 'core/data-model/constants/defaults'
import MESSAGES from 'core/data-model/constants/messages'
import VALUES from 'core/data-model/constants/values'
import MaturityValidateApi from 'core/service/insured/maturity-validate-api'
import { getServiceWrapper } from 'core/service-wrapper'
import { isIDCardNo } from 'e-submission/utils/pattern'
import { getIdentityUser } from 'identity/selectors'
import { getToggles } from 'quick-quote/feature-toggles'
import { UPDATE_BIRTHDATE } from 'quick-quote/insured-information/actions'
import {
  editSumAssured,
  editBasicPremiumState,
  UPDATE_SUM_ASSURED$,
} from 'quick-quote/product-common/coverage-plan/actions'
import { getSumAssured } from 'quick-quote/product-common/coverage-plan/selectors'
import {
  getDisplayProducts,
  getSelectedDisplayProductCode,
  getSelectedDisplayProductQuery,
} from 'quick-quote/product-selection/selectors'
import { getSelectableProductiShieldSIO } from 'quick-quote/product-sio/coverage-plan/selectors'
import { getMaturityValidate } from 'quick-quote/product-sio/selectors'
import { setLoadingStatus } from 'quick-quote/ui/actions'

import {
  MATURITY_VALIDATE,
  EDIT_MATURITY_ID_CARD,
  VALIDATE_SELECT_POLICY_OWNER,
  RESET_QUOTE_FORM_AFTER_MATURITY_VALIDATE,
  SELECT_PRODUCT_LIFE_READY_SIO,
  SELECT_PRODUCT_ISHIELD_SIO,
  setValidMaturityIDCard,
  setErrorValidateMaturityIDCard,
  setErrorValidateMaturityStatus,
  setErrorValidateSelectPolicyOwner,
  resetErrorValidateMaturityIDCard,
  resetErrorValidateSelectPolicyOwner,
  updateMaturityValidate,
  selectPolicyOwner,
  updateSumAssuredAfterMaturityValidate,
  selectBasicPlan,
} from './actions'

const validateMaturityProps = function*(action) {
  try {
    const maturityIDCard = action.payload
    const user = yield select(getIdentityUser)
    const productCode = yield select(getSelectedDisplayProductCode)
    yield put(setValidMaturityIDCard(maturityIDCard))

    yield put(setLoadingStatus(true))

    let pathURL
    if (
      Object.values(VALUES.LIFEREADY_SIO_CODE)
        .concat(Object.values(VALUES.ISHIELD_SIO_CODE))
        .concat(VALUES.CODE_12PL_SIO)
        .includes(productCode)
    ) {
      pathURL = 'product-sio-check-customer-id'
    } else if (productCode === VALUES.CODE_12PL_GIO) {
      pathURL = 'product-gio-check-customer-id'
    } else {
      pathURL = 'product-campaign/customer-maturity'
    }

    const params = {
      user,
      maturityIDCard,
      productCode,
      pathURL,
    }

    const maturityValidate = getToggles().ENABLE_BYPASS_CHECK_MATURITY_STATUS_FROM_IDCARD
      ? { maturityStatus: true, isBuyForOwner: true }
      : yield call(MaturityValidateApi.getMaturityStatus, params)
    if (maturityValidate) {
      yield put(updateMaturityValidate(maturityValidate))
      if (maturityValidate.maturityStatus === false)
        yield put(setErrorValidateMaturityStatus(MESSAGES.NOT_ALLOW_BUY_CAMPAIGN))
    }
  } catch (error) {
    yield put(updateMaturityValidate({ maturityStatus: false, isBuyForOwner: false }))
  }
  yield put(setLoadingStatus(false))
}

const validateSelectPolicyOwnerProps = function*(action) {
  const policyOwner = action.payload
  const maturityValidate = yield select(getMaturityValidate)
  yield put(selectPolicyOwner(policyOwner))
  if (policyOwner === DEFAULTS.SELF && maturityValidate.isBuyForOwner === false)
    yield put(setErrorValidateSelectPolicyOwner(MESSAGES.NOT_ALLOW_BUY_FOR_OWNER))
  else yield put(resetErrorValidateSelectPolicyOwner())
}

export const validateFormatIDCard = function*(action) {
  const maturityIDCard = action.payload
  const isIDCardCorrect = isIDCardNo(maturityIDCard.toString())
  if (!isIDCardCorrect && getToggles().ENABLE_CHECK_FORMAT_IDCARD_S7CC)
    yield put(setErrorValidateMaturityIDCard(MESSAGES.PLEASE_INPUT_CORRECT_FORMAT))
  else yield put(resetErrorValidateMaturityIDCard())
}

const resetDefaultSumAssured = function*() {
  const serviceWrapper = getServiceWrapper()
  const displayProductQuery = yield select(getSelectedDisplayProductQuery)
  const basicPlan = yield call(serviceWrapper.getBasicPlan, displayProductQuery)
  yield put(updateSumAssuredAfterMaturityValidate(basicPlan.sumAssuredMinimum))
}

/**
 * createProductSIOFunc
 * @param productCommonCoverageFunc {ReturnType<import('core/service-wrapper/legacy/handler/product-common-coverage').createProductCommonCoverageFunc>}
 */
export function createProductSIOFunc(productCommonCoverageFunc) {
  const onUpdateSelectedProductSIOWithCombineBoxPlanCode = function*(action) {
    const displayProducts = yield select(getDisplayProducts)
    const sumAssured = yield select(getSumAssured)
    const productSelected = _.find(displayProducts, { code: action.payload })
    if (productSelected) {
      yield put(selectBasicPlan(productSelected))
      yield put(editSumAssured(sumAssured))
      yield put(editBasicPremiumState(-1))
      // yield call(populateAvailableRiders)
    }
  }

  const _isLifeReadySIOMultiPlanGroup = (productCode) =>
    _.includes(VALUES.LIFE_READY_SIO_LIST_PRODUCT_MULTI_PLANCODE, productCode)
  const _isLifeReadySIO18Group = (productCode) => _.includes(VALUES.LIFE_READY_SIO_18_GROUP, productCode)
  const _isLifeReadySIO99Group = (productCode) => _.includes(VALUES.LIFE_READY_SIO_99_GROUP, productCode)

  const _getProductSIOGroupCode = (productCode) => {
    if (_isLifeReadySIO18Group(productCode)) {
      return VALUES.LIFE_READY_SIO_18_GROUP
    } else if (_isLifeReadySIO99Group(productCode)) {
      return VALUES.LIFE_READY_SIO_99_GROUP
    }
  }

  const _isValidSumAssured = (entrySumAssured, sumAssured) => {
    const { minimum, maximum } = entrySumAssured
    return !(sumAssured < minimum || sumAssured > maximum)
  }

  const switchProductSIOBySumAssured = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    if (!_isLifeReadySIOMultiPlanGroup(productCode)) return

    const sumAssured = action.payload.value
    const products = yield select(getDisplayProducts)
    const productGroupCodes = _getProductSIOGroupCode(productCode)
    const productGroup = _.filter(products, (product) => _.includes(productGroupCodes, _.get(product, 'code')))
    const product = _.find(productGroup, (product) => _isValidSumAssured(_.get(product, 'entrySumAssured'), sumAssured))
    if (product !== undefined && productCode !== _.get(product, 'code')) {
      yield put(selectBasicPlan(product))
      yield put(editSumAssured(sumAssured))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  const switchPlanProductSIOByAge = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    if (!Object.values(VALUES.ISHIELD_SIO_CODE).includes(productCode)) return

    let selectableProducts
    switch (productCode) {
      case VALUES.ISHIELD_SIO_CODE.ISHIELD_5_SIO:
      case VALUES.ISHIELD_SIO_CODE.ISHIELD_10_SIO:
      case VALUES.ISHIELD_SIO_CODE.ISHIELD_15_SIO:
      case VALUES.ISHIELD_SIO_CODE.ISHIELD_20_SIO: {
        selectableProducts = yield select(getSelectableProductiShieldSIO)
        break
      }
      default:
        selectableProducts = yield select(getSelectableProductiShieldSIO)
    }

    const selectedProductCode = selectableProducts.includes(productCode) ? productCode : selectableProducts[0]
    if (!!selectedProductCode && selectedProductCode !== productCode) {
      const displayProducts = yield select(getDisplayProducts)
      const productSelected = _.find(displayProducts, { code: selectedProductCode })
      yield put(selectBasicPlan(productSelected))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  return {
    onUpdateSelectedProductSIOWithCombineBoxPlanCode,
    switchProductSIOBySumAssured,
    switchPlanProductSIOByAge,
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* watchers(serviceWrapper) {
  const { SIO } = serviceWrapper.products
  yield [
    takeLatest([EDIT_MATURITY_ID_CARD], validateFormatIDCard),
    takeLatest([MATURITY_VALIDATE], validateMaturityProps),
    takeLatest([VALIDATE_SELECT_POLICY_OWNER], validateSelectPolicyOwnerProps),
    takeLatest([RESET_QUOTE_FORM_AFTER_MATURITY_VALIDATE], resetDefaultSumAssured),
    takeLatest(
      [SELECT_PRODUCT_LIFE_READY_SIO, SELECT_PRODUCT_ISHIELD_SIO],
      SIO.onUpdateSelectedProductSIOWithCombineBoxPlanCode
    ),
    takeLatest([UPDATE_SUM_ASSURED$], SIO.switchProductSIOBySumAssured),
    takeLatest([UPDATE_BIRTHDATE], SIO.switchPlanProductSIOByAge),
  ]
}

export const sagas = watchers
